import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Menu,
  MenuItem,
} from "@mui/material";
import { Add, Remove, Schedule } from "@mui/icons-material";
import {
  startTimeToMinutes,
  endTimeToMinutes,
  minutesToTime,
} from "../../utils/timeHelpers";

export interface TimeCardProps {
  label: string;
  value: string; // HH:mm format (e.g., "09:00")
  onChange: (value: string) => void;
  minTime?: string; // Minimum allowed time (HH:mm)
  maxTime?: string; // Maximum allowed time (HH:mm) - can be after midnight
  step?: number; // Minutes step for +/- buttons (default: 30)
  format?: "12h" | "24h"; // Time display format (default: "24h")
  disabled?: boolean;
  showIcon?: boolean;
  color?: "primary" | "secondary" | "success" | "error" | "warning" | "info";
}

// Format time for display based on format preference
const formatTimeDisplay = (time: string, format: "12h" | "24h"): string => {
  const [hours, minutes] = time.split(":").map(Number);

  if (format === "24h") {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

// Generate time options for dropdown (handles overnight)
const generateTimeOptions = (
  minTime: string,
  maxTime: string,
  step: number
): string[] => {
  const options: string[] = [];
  const minMinutes = startTimeToMinutes(minTime);
  const maxMinutes = endTimeToMinutes(maxTime);

  for (let m = minMinutes; m <= maxMinutes; m += step) {
    options.push(minutesToTime(m));
  }

  return options;
};

export default function TimeCard({
  label,
  value,
  onChange,
  minTime = "00:00",
  maxTime = "23:59",
  step = 30,
  format = "24h",
  disabled = false,
  showIcon = true,
  color = "primary",
}: TimeCardProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  // Use utility functions for proper overnight handling
  const minMinutes = startTimeToMinutes(minTime);
  const maxMinutes = endTimeToMinutes(maxTime);
  const currentMinutes = startTimeToMinutes(value);

  // Adjust current minutes if it's after midnight and we're in overnight mode
  const adjustedCurrentMinutes = currentMinutes < minMinutes
    ? currentMinutes + 1440
    : currentMinutes;

  // Check if we can increment/decrement
  const canIncrement = adjustedCurrentMinutes + step <= maxMinutes;
  const canDecrement = adjustedCurrentMinutes - step >= minMinutes;

  // Generate time options for quick select
  const timeOptions = useMemo(
    () => generateTimeOptions(minTime, maxTime, step),
    [minTime, maxTime, step]
  );


  // Handle increment
  const handleIncrement = () => {
    if (canIncrement && !disabled) {
      const newMinutes = adjustedCurrentMinutes + step;
      onChange(minutesToTime(newMinutes));
    }
  };

  // Handle decrement
  const handleDecrement = () => {
    if (canDecrement && !disabled) {
      const newMinutes = adjustedCurrentMinutes - step;
      onChange(minutesToTime(newMinutes));
    }
  };

  // Handle menu open
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setMenuAnchor(event.currentTarget);
    }
  };

  // Handle menu close
  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  // Handle time selection from menu
  const handleSelectTime = (time: string) => {
    onChange(time);
    handleCloseMenu();
  };

  const buttonSize = 36;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          transition: "all 0.2s",
          opacity: disabled ? 0.6 : 1,
          "&:hover": disabled ? {} : { borderColor: `${color}.main`, boxShadow: 1 },
        }}
      >
        {/* Icon */}
        {showIcon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              bgcolor: `${color}.100`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Schedule sx={{ color: `${color}.main`, fontSize: 24 }} />
          </Box>
        )}

        {/* Time Display - clickable to open menu */}
        <Box
          onClick={handleOpenMenu}
          sx={{
            flex: 1,
            cursor: disabled ? "default" : "pointer",
            userSelect: "none",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={600} color={`${color}.main`} sx={{ lineHeight: 1.2 }}>
            {formatTimeDisplay(value, format)}
          </Typography>
        </Box>

        {/* Increment/Decrement Buttons */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {/* Increment Button */}
          <Box
            onClick={handleIncrement}
            sx={{
              width: buttonSize,
              height: buttonSize,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid",
              borderColor: canIncrement && !disabled ? `${color}.main` : "grey.300",
              borderRadius: 1,
              bgcolor: canIncrement && !disabled ? `${color}.50` : "grey.100",
              cursor: canIncrement && !disabled ? "pointer" : "not-allowed",
              transition: "all 0.15s",
              "&:hover": canIncrement && !disabled ? {
                bgcolor: `${color}.100`,
                transform: "scale(1.05)",
              } : {},
              "&:active": canIncrement && !disabled ? {
                bgcolor: `${color}.200`,
                transform: "scale(0.95)",
              } : {},
            }}
          >
            <Add
              sx={{
                fontSize: 22,
                color: canIncrement && !disabled ? `${color}.main` : "grey.400",
              }}
            />
          </Box>

          {/* Decrement Button */}
          <Box
            onClick={handleDecrement}
            sx={{
              width: buttonSize,
              height: buttonSize,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid",
              borderColor: canDecrement && !disabled ? `${color}.main` : "grey.300",
              borderRadius: 1,
              bgcolor: canDecrement && !disabled ? `${color}.50` : "grey.100",
              cursor: canDecrement && !disabled ? "pointer" : "not-allowed",
              transition: "all 0.15s",
              "&:hover": canDecrement && !disabled ? {
                bgcolor: `${color}.100`,
                transform: "scale(1.05)",
              } : {},
              "&:active": canDecrement && !disabled ? {
                bgcolor: `${color}.200`,
                transform: "scale(0.95)",
              } : {},
            }}
          >
            <Remove
              sx={{
                fontSize: 22,
                color: canDecrement && !disabled ? `${color}.main` : "grey.400",
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Time Selection Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: 120,
            borderRadius: 2,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {timeOptions.map((time) => (
          <MenuItem
            key={time}
            onClick={() => handleSelectTime(time)}
            selected={time === value}
            sx={{
              py: 1,
              "&.Mui-selected": {
                bgcolor: `${color}.50`,
                fontWeight: 600,
                "&:hover": {
                  bgcolor: `${color}.100`,
                },
              },
            }}
          >
            <Typography variant="body2">
              {formatTimeDisplay(time, format)}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
